import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

import { RootState } from "../../redux/store";
import { UserState, UserResponse, User, ApiError } from "./types/user-types";

const initialState: UserState = {
  users: [],
  leaderboard: [],
  user: null,
  loading: false,
  error: null,
  success: false,
  total: 0,
  perPage: 10,
  currentPage: 1,
  lastPage: 1,
};

// API Base URL
const API_URL = process.env.REACT_APP_API_URL;

// ✅ Create User
export const createUser = createAsyncThunk<
  UserResponse,
  Partial<User>,
  { rejectValue: ApiError; state: RootState }
>("users/create", async (userData, thunkAPI) => {
  const token = thunkAPI.getState().auth.token;
  if (!token) return thunkAPI.rejectWithValue({ message: "Unauthorized" });

  try {
    const res = await axios.post(`${API_URL}/users`, userData, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data;
  } catch (err: any) {
    return thunkAPI.rejectWithValue(
      err.response?.data || { message: "Failed to create user" }
    );
  }
});

// ✅ Fetch Users (Paginated)
export const fetchUsers = createAsyncThunk<
  UserResponse,
  { page?: number },
  { rejectValue: ApiError; state: RootState }
>("users/fetch", async ({ page = 1 }, thunkAPI) => {
  const token = thunkAPI.getState().auth.token;
  if (!token) return thunkAPI.rejectWithValue({ message: "Unauthorized" });

  try {
    const res = await axios.get(`${API_URL}/users?page=${page}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data;
  } catch (err: any) {
    return thunkAPI.rejectWithValue(
      err.response?.data || { message: "Failed to fetch users" }
    );
  }
});

// ✅ Fetch Single User by ID
export const fetchUserById = createAsyncThunk<
  User,
  number,
  { rejectValue: ApiError; state: RootState }
>("users/fetchById", async (user_id, thunkAPI) => {
  const token = thunkAPI.getState().auth.token;
  if (!token) return thunkAPI.rejectWithValue({ message: "Unauthorized" });

  try {
    const res = await axios.get(`${API_URL}/users/${user_id}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data.user;
  } catch (err: any) {
    return thunkAPI.rejectWithValue(
      err.response?.data || { message: "Failed to fetch user" }
    );
  }
});

// ✅ Delete User
export const deleteUser = createAsyncThunk<
  number,
  number,
  { rejectValue: ApiError; state: RootState }
>("users/delete", async (user_id, thunkAPI) => {
  const token = thunkAPI.getState().auth.token;
  if (!token) return thunkAPI.rejectWithValue({ message: "Unauthorized" });

  try {
    await axios.delete(`${API_URL}/users/${user_id}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return user_id;
  } catch (err: any) {
    return thunkAPI.rejectWithValue(
      err.response?.data || { message: "Failed to delete user" }
    );
  }
});

// ✅ Update User
export const updateUser = createAsyncThunk<
  User,
  { user_id: number; data: Partial<User> },
  { rejectValue: ApiError; state: RootState }
>("users/update", async ({ user_id, data }, thunkAPI) => {
  const token = thunkAPI.getState().auth.token;
  if (!token) return thunkAPI.rejectWithValue({ message: "Unauthorized" });

  try {
    const res = await axios.put(`${API_URL}/users/${user_id}`, data, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data.user;
  } catch (err: any) {
    return thunkAPI.rejectWithValue(
      err.response?.data || { message: "Failed to update user" }
    );
  }
});

export const disconnectGoogle = createAsyncThunk<
  { message: string; user: any },
  void,
  { rejectValue: ApiError; state: RootState }
>("users/disconnectGoogle", async (_, thunkAPI) => {
  const token = thunkAPI.getState().auth.token;
  if (!token) return thunkAPI.rejectWithValue({ message: "Unauthorized" });

  try {
    const res = await axios.put(
      `${API_URL}/user/google/disconnect`,
      {},
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );
    console.log("disconnectGoogle response:", res.data);
    return res.data;
  } catch (err: any) {
    return thunkAPI.rejectWithValue(
      err.response?.data || { message: "Failed to disconnect Google" }
    );
  }
});

export const fetchLeaderboard = createAsyncThunk<
  { data: User[] },
  string, // 'monthly' | 'allTime'
  { rejectValue: ApiError; state: RootState }
>("users/fetchLeaderboard", async (type, thunkAPI) => {
  const token = thunkAPI.getState().auth.token;
  try {
    const res = await axios.get(`${API_URL}/leaderboard?type=${type}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data;
  } catch (err: any) {
    return thunkAPI.rejectWithValue(
      err.response?.data || { message: "Failed to fetch leaderboard" }
    );
  }
});

// ✅ Slice
const userSlice = createSlice({
  name: "users",
  initialState,
  reducers: {
    resetUserState: (state) => {
      state.loading = false;
      state.error = null;
      state.success = false;
      state.user = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Create
      .addCase(createUser.pending, (state) => {
        state.loading = true;
        state.success = false;
        state.error = null;
      })
      .addCase(createUser.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        if (action.payload.user) {
          state.users.unshift(action.payload.user);
        }
      })
      .addCase(createUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || "Failed to create user";
      })

      // Fetch All
      .addCase(fetchUsers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUsers.fulfilled, (state, action) => {
        state.loading = false;
        state.users = action.payload.data || [];
        state.total = action.payload.total || 0;
        state.perPage = action.payload.per_page || 10;
        state.currentPage = action.payload.current_page || 1;
        state.lastPage = action.payload.last_page || 1;
      })
      .addCase(fetchUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || "Failed to fetch users";
      })

      // Fetch One
      .addCase(fetchUserById.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.user = null;
      })
      .addCase(fetchUserById.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload;
      })
      .addCase(fetchUserById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || "Failed to fetch user";
      })

      // Delete
      .addCase(deleteUser.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(deleteUser.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.users = state.users.filter(
          (user) => user.user_id !== action.payload
        );
      })
      .addCase(deleteUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || "Failed to delete user";
      })

      // Update
      .addCase(updateUser.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(updateUser.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;

        // Update user in list
        state.users = state.users.map((user) =>
          user.user_id === action.payload.user_id ? action.payload : user
        );

        // Update selected user if it's the one updated
        if (state.user?.user_id === action.payload.user_id) {
          state.user = action.payload;
        }
      })
      .addCase(updateUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || "Failed to update user";
      })
      .addCase(disconnectGoogle.pending, (state) => {
        state.loading = true;
      })
      .addCase(disconnectGoogle.fulfilled, (state, action) => {
        state.loading = false;

        console.log("disconnectGoogle in userSlice:", action.payload);

        // Update user if it exists in userSlice
        if (state.user && action.payload.user) {
          state.user = action.payload.user;
        }
      })
      .addCase(disconnectGoogle.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || "Failed to disconnect Google";
      })
      .addCase(fetchLeaderboard.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchLeaderboard.fulfilled, (state, action) => {
        state.loading = false;
        state.leaderboard = action.payload.data;
      })
      .addCase(fetchLeaderboard.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || "Failed to fetch leaderboard";
      });
  },
});

export const { resetUserState } = userSlice.actions;
export default userSlice.reducer;
